import React, { useState, useEffect, useCallback, memo } from "react";
import { Container, Header, Button } from "semantic-ui-react";
import { PortalMessage } from "components/Admin/PortalMessage";
import { apiGatewayClientWithCredentials } from "services/api";
import { auditStore } from "services/state";
import AuditTable from "components/Admin/Audit/AuditTable";
import { SidebarOverlay } from "components/Admin/Audit/SidebarOverlay";
import { PAGE_SIZE } from "components/Admin/Audit/audit-config";
import { TableFilter } from "components/Admin/TableFilters";

function mapQuery(queryObj){
  const obj = {};
  for (const key in queryObj) {
    if (Object.prototype.hasOwnProperty.call(queryObj, key) && queryObj[key]) {
      const element = queryObj[key];
      if(element !== undefined) obj[key] = element;
    }
  }
  return obj;
}

const TableHeader = memo(
  ({ datePickerInfo, isSelected, onChangeTextFilter, onChangeDateFilter, onSearch, onViewAuditDetails }) => (
    <div className='table-header'>
      <TableFilter
        datePickerInfo={datePickerInfo}
        onChangeTextFilter={onChangeTextFilter}
        onChangeDateFilter={onChangeDateFilter}
        onSearch={onSearch.bind(null, 'filter')}
      />
      <Button content='Search' onClick={onSearch} />
      <Button className='view' content='View' disabled={!isSelected} onClick={onViewAuditDetails} />
    </div>
  )
);

const Audit = () => {
  const defaultPagination = {
    currentPage: 1,
    totalPages: 0,
  };
  const [auditList, setAuditData] = useState([]);
  const [paginatorInfo, setPageInfo] = useState(defaultPagination);
  const [searchData, setSearchData] = useState({
    user: "",
    operator: "",
    action: "",
    data: "",
    start: "",
    end: "",
  });
  const [datePickerInfo, setDatepickerData] = useState({
    start: null,
    end: null,
  });
  const [showSuccessMes, setShowSuccessMes] = useState("");
  const [showFailMes, setShowFailMes] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [isLoaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    updateAuditData();
  }, []);

  const onChangeTextFilter = useCallback(
    (type, e, data) => {
      setSearchData((prevState) => ({ ...prevState, [type]: data.value.trim()}));
    },
    [searchData]
  );

  const onChangeDateFilter = useCallback(
    (type, date) => {
      setSearchData((prevState) => ({ ...prevState, [type]: date?.toISOString() || null }));
      setDatepickerData({ ...datePickerInfo, [type]: date });
    },
    [searchData]
  );

  const onPageChange = useCallback((e, data) => {
    updateAuditData({ ...searchData, currentPage: data.activePage });
    setSelectedItem({});
  }, [searchData]);

  const onSelect = useCallback((data) => setSelectedItem(data), []);

  const onViewAuditDetails = useCallback(() => {
    setVisible(true);
    auditStore.isOpen = true;
  }, []);

  const closeSidebar = useCallback(() => {
    setVisible(false);
    auditStore.isOpen = false;
  }, []);

  const onSearch = useCallback((type,ev) => {
    const isHitEnter = type==='filter' && ev.key === 'Enter';
    const isHitSearchBtn = typeof type === 'object';
    if( isHitEnter || isHitSearchBtn){
      updateAuditData(searchData);
      setSelectedItem({});
      setPageInfo(defaultPagination)
    }
  }, [searchData]);

  const getAuditData = async ({ start, end, user, operator, data, action, currentPage = 1, limit = PAGE_SIZE }) => {
    const apiGatewayClient = await apiGatewayClientWithCredentials();
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    setLoaded(false);
    const queryParams = mapQuery({ user, operator, data, action, limit, currentPage });
    return await apiGatewayClient
      .get("/admin/audit-list", params, {}, { queryParams })
      .then((res) => {
        let { auditList, count } = res.data;
        auditList =  transformDataForm(auditList)
        return {
          success: true,
          auditList,
          totalPages: Math.ceil(count / PAGE_SIZE),
        };
      })
      .catch((err) => {
        console.log("audit list err", err);
        return {
          success: false,
          auditList: [],
          totalPages: 1,
        };
      });
  };

  const transformDataForm = (auditList) => {
    const t1 = new Date().getTime();
    auditList.forEach((item, index) => {
      const action = item.Action;
      let data = item.Data;
      if (action === "Add Host" || action === "Delete Host") {
        const { HostId, Host, MES, Envide, Scope, Stage, Name } = data;
        item.Data = {
          HostId,
          Host,
          Platform: MES,
          Envide,
          Scope,
          Stage,
          Alias: Name,
        };
      } else if (action === "Add Profile") {
        const {ProfileId, ProfileName, MES, Scope, Stage, Host, Description, CreateDate} = data;
        item.Data = {
          ProfileId,
          ProfileName,
          Platform: MES,
          Scope,
          Stage,
          Host,
          Description,
          CreateDate,
        };
      } else if (action === "Delete Profile") {
        const {ProfileId, ProfileName, MES, Scope, Stage, Host, Permission, Description, CreateDate} = data;
        item.Data = {
          ProfileId,
          ProfileName,
          Platform: MES,
          Scope,
          Stage,
          Host,
          Description,
          CreateDate,
          Permission,
        };
      } else if (action === "Update Profile") {
        const {Scope, Stage, Host, ProfileName, Description, Permission} = data;
        item.Data = {
          Scope,
          Stage,
          Host,
          ProfileName,
          Description,
          Permission,
        };
      } else if (action === "Update MES15 Target") {
        const {Target, Stage} = data;
        item.Data = {
          Stage,
          Target,
        };
      } else if (action === "Update MES15 App Profile") {
        
        if (data.Profile && data.Profile.length) {
          data.Profile.forEach((p,i) => {
            const { Scope, Stage, ProfileId, ProfileName } = p;
            data.Profile[i] = {
              Scope,
              Stage,
              ProfileName,
              ProfileId,
            };
          });
        }
       
      } else if(action === "Add App") {
        const {UserName, AppName, ClientId, OauthScope, Profile} = data;
        item.Data = {
          UserName,
          AppName,
          ClientId,
          OauthScope,
          Profile,
        };
      } else if (action === "Delete App") {
        const {UserName, AppName, ClientId, OauthScope, MES15, MES16} = data;
        item.Data = {
          UserName,
          AppName,
          ClientId,
          OauthScope,
          MES15,
          MES16,
        };
        if (MES16 && MES16.Hosts && MES16.Hosts.length) {
          MES16.Hosts.forEach((h, i) => {
            const {Host, Target, Scope, Stage, ProfileId} = h;
            MES16.Hosts[i] = {Host, Scope, Stage, Target, ProfileId};
          })
        }
        if (MES15 && MES15.Profiles && MES15.Profiles.length) {
          MES15.Profiles.forEach((p, i) => {
            const {ProfileId, ProfileName, Scope, Stage, Host} = p;
            MES15.Profiles[i] = {ProfileId, ProfileName, Scope, Stage, Host};
          })
        }
      } else if (action === "Delete MES16 App Host") {
        const {UserName, AppName, AppId, OauthScope, Host} = data;
        item.Data = {
          UserName,
          AppName,
          ClientId: AppId,
          OauthScope,
          Host,
        };
        if (data.Host && data.Host.length) {
          data.Host.forEach((h,i) => {
            const {Stage, Host, Target, Scope, Profile} = h;
            data.Host[i] = {Host, Scope, Stage, Target, ProfileId: Profile}
          })
        }
      } else if (action === "Update Host") {
        const {Host, Name} = data;
        item.Data = {
          Host,
          Alias: Name,
        };
      } else if (action === "Add MES16 App Host") {
        if (data.Host && data.Host.length) {
          data.Host.forEach((h, i) => {
            const {Host, Target, Scope, Stage, ProfileId} = h;
            data.Host[i] = {Host, Scope, Stage, Target, ProfileId}
          })
        }
      } else if (action === "Update MES16 App Host") {
        const {UserName, AppName, ClientId, OauthScope, Host} = data;
        item.Data = {
          UserName,
          AppName,
          ClientId,
          OauthScope,
          Host,
        };
        if (data.Host && data.Host.length) {
          data.Host.forEach((h, i) => {
            const {Host, Target, Scope, Stage, ProfileId} = h;
            data.Host[i] = {Host, Scope, Stage, Target, ProfileId}
          })
        }
      }

      const {Date, Data, User, Id, Operator, Action} = item;
      auditList[index] = {
        Date,
        Data,
        User,
        Operator,
        Action,
        Id,
      };
    });
    return auditList;
  };
  

  const updateAuditData = async (params = { currentPage: 1 }) => {
    const { start, end } = params;
    setShowFailMes("");
    if (start && new Date(start) >= new Date()) {
      setShowFailMes("The start time should be earlier than current time!");
      return;
    }
    if (start && end && new Date(start) >= new Date(end)) {
      setShowFailMes("The start time should be ealier than the end time!");
      return;
    }
    const data = await getAuditData(params);
    const { auditList, totalPages, success, message } = data;
    setAuditData(auditList);
    setPageInfo({ currentPage: params.currentPage, totalPages });
    setLoaded(true);
    if (!success) {
      setShowFailMes(message ? message : "Ooh! Something went wrong!");
    }
  };
  return (
    <Container fluid style={{ padding: "2em", position: "relative" }} className={visible ? "is-active" : null}>
      <Header as='h1'>Audit</Header>
      <PortalMessage
        showSuccessMes={showSuccessMes}
        showFailMes={showFailMes}
        setShowSuccessMes={setShowSuccessMes}
        setShowFailMes={setShowFailMes}
      />
      <TableHeader
        datePickerInfo={datePickerInfo}
        onChangeTextFilter={onChangeTextFilter}
        onChangeDateFilter={onChangeDateFilter}
        onSearch={onSearch}
        onViewAuditDetails={onViewAuditDetails}
        isSelected={Object.keys(selectedItem).length > 0}
      />
      <AuditTable
        auditList={auditList}
        paginatorInfo={paginatorInfo}
        isLoaded={isLoaded}
        selectedItem={selectedItem}
        onPageChange={onPageChange}
        onSelect={onSelect}
      />
      <SidebarOverlay visible={visible} onClose={closeSidebar}>
        {selectedItem}
      </SidebarOverlay>
    </Container>
  );
};

export default Audit;
